"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateAccumulationRequests = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const __1 = require("../..");
const types_1 = require("../../types");
const __2 = require("../..");
const __3 = require("../..");
const prettyAbh = (abh) => {
    let abhStr = ``;
    const states = abh.getStateSnapshotArray();
    for (const state of states) {
        abhStr += `${(0, __1.getNiceDate)(state.timestamp)}: ${JSON.stringify(state.state)}\n`;
    }
    return abhStr;
};
/** Helper fn for generating accumulation requests from inside CalculateState
 * Finds completed hours by using an autobuckethistory
 *
   @param currentTimestamp - Timestamp of the current message
   @param entity - The entity connected to the accumulator
   @param currentMessage - The values in the current message being accumulated in CalculateState
   @param recentStateHistory - Record of all readings of this entity
   @param logger - Logger for errors etc
   @param accumulatorClass - The type of accumulator to generate requests for (right now only HourlyYearBased supported)
   @param subHourTzOffset - For hourly year based normal accumulators, the logic is tweaked if the tz offset from UTC is not an even number of hours.
   @param bucketStrategyMap - Map of reading keys to bucket strategies. Defaults to Apportion if not present
 * */
const generateAccumulationRequests = (currentTimestamp, entity, currentMessage, recentStateHistory, logger, accumulatorClass, subHourTzOffset = 0, bucketStrategyMap) => {
    logger.debug('Generating Normal Accumulations for', (0, __1.getNiceDate)(currentTimestamp), currentMessage);
    if (accumulatorClass !== iotv_v3_types_1.AccumulatorClasses.HourlyYearBasedNormalAccumulator) {
        logger.error(`Attempting to generate accumulation requests with unknown accumulator class: ${accumulatorClass}`);
        return;
    }
    const timeBucketDef = { width: 1, unit: __1.TimeBucketUnits.HOUR, offset: subHourTzOffset };
    /** Bucket this message */
    const curAbh = new __1.AutoBucketHistory({
        timeBucketDef,
        defaultBucketStrategy: types_1.BucketStrategy.APPORTION,
    });
    if (bucketStrategyMap) {
        curAbh.loadKeyStrategyMap(bucketStrategyMap);
    }
    const nonNullTimestampsBeforeCur = recentStateHistory.getIndexLastNonNullKeyLessThanTimestamp(currentTimestamp, 'timestamps');
    curAbh.pushKeywise({ timestamp: currentTimestamp, state: currentMessage }, nonNullTimestampsBeforeCur);
    logger.debug(`Cur Message:\n${prettyAbh(curAbh)}`);
    /** Get time bucket containing latest timestamp */
    // If the timestamp is equal to the very end of the bucket, then we know it's done. See comment 2 lines below
    const timeBucketOfCurrentTimestamp = (0, __2.getApportionToTimeBucketsMap)({
        timeBucketDef,
        measureRange: { fromTimestamp: currentTimestamp, toTimestamp: currentTimestamp + 1 }, // +1 gives us the smallest tiny overlap. Ensures that a timestamp like 12:00 is in the 12:00-13:00 bucket. This works here because if the time is exactly 12:00, we know bucket 11:00-12:00 is complete, because no possible additions can be made to 11:00.
    });
    const curBucketTs = timeBucketOfCurrentTimestamp[0].bucket[0];
    logger.debug('Bucket of Current TS', (0, __1.getNiceDate)(curBucketTs));
    logger.debug('First Ts in Bucketed', (0, __1.getNiceDate)(curAbh.timestamp[0]));
    /** Right now we've only bucketed the most recent message, but we want to get the full value of the earliest bucket, so we actually need to bucket the RSH (plus the current messages, because the RSH doesn't have them yet). */
    const relevantReadings = [
        ...recentStateHistory.sliceByTimestamp([curAbh.timestamp[0], currentTimestamp]).getStateSnapshotArray(), // I think slicing by timestamp might be off by 1
        //...curAbh.getStateSnapshotArray(),
        // { timestamp: currentTimestamp, state: currentMessage }, (CRA: assume RSH is updated with the current message)
    ]; //.filter((snap) => snap.timestamp < curBucketTs); // We can use the curAbh timestamp here because it will be the start of the first bucket, which we are reaching into the RSH to find.
    logger.debug('Relevant Readings', relevantReadings, (0, __1.getNiceDates)(relevantReadings.map((r) => r.timestamp)));
    if (relevantReadings.length === 0)
        return [];
    const relevantBuckets = new __1.AutoBucketHistory({ timeBucketDef, defaultBucketStrategy: types_1.BucketStrategy.APPORTION });
    // Find the latest non null timestamps before the relevant readings, so that bucketing the RSH is accurate
    const nonNullTimestampsBeforeRelevant = recentStateHistory.getIndexLastNonNullKeyLessThanTimestamp(curAbh.timestamp[0], 'timestamps');
    // Bucket the RSH with all readings from candidate buckets
    relevantBuckets.pushKeywise(relevantReadings[0], nonNullTimestampsBeforeRelevant);
    for (let idx = 1; idx < relevantReadings.length; idx++) {
        relevantBuckets.pushKeywise(relevantReadings[idx]);
    }
    // Slice by timestamp gets all timestamps between. But timestamps of bucketed readings represent the latest point of that reading. That means it's possible a relevant reading gets apportioned into a bucket before the latest incomplete bucket.
    // For this reason, we filter not only by buckets whose timestamps are before the bucket of the current timestamp, but also timestamps that are later (or equal) than the earliest bucket found when we bucket the current message (the latest known to not be accumulated yet).
    // Fun Fact: We actually also filter on this when we only accumulate readings whose latest timestamp needs to be within or before the bucket. So as long as we don't include accumulation requests who have an empty update, the second part of the filter here is actually unneeded.
    // I have both though, because the time spent understanding this problem demands some material difference in the file.
    // Now we have the full range of values
    const completeBuckets = relevantBuckets.getStateSnapshotArray().filter((s) => s.timestamp < curBucketTs && s.timestamp >= curAbh.timestamp[0]);
    logger.debug('Complete Buckets', completeBuckets, (0, __1.getNiceDates)(completeBuckets.map((s) => s.timestamp)));
    // Turn complete buckets into Accumulation Requests (normals)
    // Some keys may be in the earliest bucket but their latest reading is after. So we need to make sure we only accumulate keys who's latest timestamp before the **current** message is in or before the bucket
    let accumulationRequests = [];
    completeBuckets.forEach((completeBucket) => {
        let request = {
            accessor: (0, __3.hourlyYearBasedAccessorFromTimestamp)(completeBucket.timestamp),
            accumulationClassName: iotv_v3_types_1.AccumulatorClasses.HourlyYearBasedNormalAccumulator,
            update: {},
            validBusinessObject: entity,
        };
        Object.entries(completeBucket.state).forEach(([readingKey, value]) => {
            if (value !== null && (nonNullTimestampsBeforeCur[readingKey] ?? -Infinity) < completeBucket.timestamp + iotv_v3_types_1.Milliseconds.HOUR) {
                request.update[readingKey] = value;
            }
        });
        if (Object.keys(request.update).length > 0)
            accumulationRequests.push(request);
    });
    return accumulationRequests;
};
exports.generateAccumulationRequests = generateAccumulationRequests;
